import { AlertColor } from '@mui/material'
import {
  PayloadAction,
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit'
import { RootState } from '.'
import okaoAxios from '../helpers/axios'
import axios from 'axios'
import {
  DiningReview,
  Eater,
  IRestaurantReview,
  InstagramPointsResponse,
  Restaurant,
  RestaurantAndPredictedRestaurant,
  StoryRequest,
} from '../types'
import { RewardsActivityResponse } from '../types/rewardsTypes'
import { PointsHistoryItemDTO } from '../types/pointsHistoryTypes'
import { mapVoucherifyActivityResponse } from '../helpers/voucherifyActivityMapper'
import {
  fetchSession,
  recoverUserAccount,
  restaurantOnboardRequest,
  updateRestaurantOnboard,
} from './authSlice'
import { updateEaterProfile } from './onboardSlice'
import { favoriteRestaurant, saveRestaurantDish } from './restaurantSlice'
import { getSavedMenus, type SavedMenusResponse } from '../api/menuScanApi'

export interface IProfileSlice {
  isProfileEdit: boolean
  isValidData: boolean
  isUpdate: boolean
  isEditCancelled: boolean
  currentTab: string
  status: string
  shouldOpen: boolean
  isProfileLoading: boolean
  message: string | undefined
  severity: AlertColor | undefined
  eater: Eater | undefined
  savedRestaurantIds: string[]
  bioPayload: Eater
  eaterDishReviews: DiningReview[]
  profileDishReviews: {
    reviews: Array<{
      dishId: string
      dishName: string
      restaurantAlias: string
      restaurantName: string
      starCount: number
      review?: string
      created: string
      updated: string
    }>
    loading: boolean
    error: string | null
    page: number
    limit: number
    totalCount: number
    hasMore: boolean
  }
  profileRestaurantReviews: {
    reviews: IRestaurantReview[]
    loading: boolean
    error: string | null
    page: number
    limit: number
    totalCount: number
    hasMore: boolean
  }
  savedRestaurants: {
    restaurants: RestaurantAndPredictedRestaurant[]
    loading: boolean
    error: string | null
    lastFetched: number | null
  }
  rewardsActivity: {
    activities: any[]
    loading: boolean
    error: string | null
    page: number
    hasMore: boolean
    lastFetched: number | null
  }
  savedMenus: {
    items: Array<{
      savedId: string
      type: 'RESTAURANT_MENU' | 'SCAN_ONLY'
      savedAt: string
      menuId: string | null
      menuName: string
      restaurantId: string | null
      restaurantName: string | null
      restaurantAlias: string | null
      dishCount: number
      sampleDishes: string[]
      hasImage: boolean
      imageUrl: string | null
    }>
    loading: boolean
    error: string | null
    page: number
    size: number
    totalElements: number
    totalPages: number
    hasMore: boolean
    lastFetched: number | null
  }
}

const initialState: IProfileSlice = {
  isProfileEdit: false,
  isValidData: false,
  isUpdate: false,
  isEditCancelled: false,
  isProfileLoading: false,
  currentTab: 'Bio',
  status: 'idle',
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  eater: undefined,
  bioPayload: {},
  savedRestaurantIds: [],
  eaterDishReviews: [],
  profileDishReviews: {
    reviews: [],
    loading: false,
    error: null,
    page: 0,
    limit: 20,
    totalCount: 0,
    hasMore: false,
  },
  profileRestaurantReviews: {
    reviews: [],
    loading: false,
    error: null,
    page: 0,
    limit: 20,
    totalCount: 0,
    hasMore: false,
  },
  savedRestaurants: {
    restaurants: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  rewardsActivity: {
    activities: [],
    loading: false,
    error: null,
    page: 0,
    hasMore: false,
    lastFetched: null,
  },
  savedMenus: {
    items: [],
    loading: false,
    error: null,
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    hasMore: false,
    lastFetched: null,
  },
}

export const updateEater = createAsyncThunk(
  'api/updateEater',
  async (story: StoryRequest) => {
    const response = await okaoAxios.put(`/eater/update-eater`, story)
    return response?.data
  },
)
export const fetchEater = createAsyncThunk('api/fetchEater', async () => {
  const response = await okaoAxios.get(
    `${process.env.REACT_APP_SERVICE_URL ?? window.location.origin}/eater?includeEarnWays=true&includePredictionScores=true`,
  )
  return response?.data
})

export const updateEaterImage = createAsyncThunk(
  'api/updateEaterImage',
  async (formData: FormData) => {
    const response = await okaoAxios.post(`/api/upload/eater`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    const data = {
      ...response.data,
      headers: { ...response.headers },
    }
    return data
  },
)

export const fetchRewardsActivity = createAsyncThunk<
  RewardsActivityResponse,
  { page?: number; limit?: number; refresh?: boolean },
  { rejectValue: { message: string; status?: number } }
>(
  'api/fetchRewardsActivity',
  async ({ page = 0, limit = 50 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('limit', limit.toString())
      
      const url = `/api/rewards/activity?${params.toString()}`
      
      const response = await okaoAxios.get(url)
      const data = response?.data
      
      // Sanitize response to remove Voucherify credentials
      if (data && Array.isArray(data.data || data.activities || data.items)) {
        const activities = data.data || data.activities || data.items
        const sanitized = activities.map((activity: any) => {
          const { customerId, appId, appToken, ...sanitizedActivity } = activity
          return sanitizedActivity
        })
        return {
          ...data,
          data: sanitized,
          activities: sanitized,
          items: sanitized,
        }
      }
      
      return data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          message: error.response?.data?.message || 'Unable to load activity. Try again.',
          status: error.response?.status,
        })
      }
      return rejectWithValue({ message: 'Unable to load activity. Try again.' })
    }
  },
)

/**
 * Fetch saved menus for the authenticated user.
 */
export const fetchSavedMenus = createAsyncThunk<
  SavedMenusResponse,
  { page?: number; size?: number; sort?: 'recent' | 'name'; refresh?: boolean },
  { rejectValue: { message: string; status?: number } }
>(
  'api/fetchSavedMenus',
  async ({ page = 0, size = 20, sort = 'recent' }, { rejectWithValue }) => {
    try {
      const response = await getSavedMenus({ page, size, sort })
      return response
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          message: error.response?.data?.message || 'Unable to load saved menus. Try again.',
          status: error.response?.status,
        })
      }
      return rejectWithValue({ message: 'Unable to load saved menus. Try again.' })
    }
  },
)

export interface SendPayoutLinkSmsRequest {
  paymentLinkUrl: string
}

export interface SendPayoutLinkSmsResponse {
  status: string
}

export const sendPayoutLinkSms = createAsyncThunk<
  SendPayoutLinkSmsResponse,
  SendPayoutLinkSmsRequest,
  { rejectValue: { message: string } }
>(
  'wallet/sendPayoutLinkSms',
  async (request, { rejectWithValue }) => {
    try {
      const { sendPayoutLinkSms: sendSms } = await import('../services/walletPayoutService')
      const response = await sendSms(request.paymentLinkUrl)
      return response
    } catch (error: any) {
      return rejectWithValue({
        message: error?.response?.data?.message || 'Failed to send SMS',
      })
    }
  },
)

export const awardInstagramPoints = createAsyncThunk<
  InstagramPointsResponse,
  undefined,
  { rejectValue: { message: string; alreadyClaimed?: boolean } }
>('api/awardInstagramPoints', async (_, { rejectWithValue }) => {
  try {
    const response = await okaoAxios.post<InstagramPointsResponse>(
      '/api/points/instagram-engagement',
    )
    return response.data
  } catch (error: any) {
    const status = error.response?.status
    const message = error.response?.data?.message || 'Failed to award Instagram points'
    const alreadyClaimed = status === 409 || message.toLowerCase().includes('already claimed')
    return rejectWithValue({ message, alreadyClaimed })
  }
})

export interface FetchEaterRestaurantReviewsRequest {
  eaterId: string
  page?: number
  limit?: number
}

export interface FetchEaterRestaurantReviewsResponse {
  result: IRestaurantReview[]
  page: number
  count: number
  totalCount: number
}

export const fetchEaterRestaurantReviews = createAsyncThunk<
  FetchEaterRestaurantReviewsResponse,
  FetchEaterRestaurantReviewsRequest,
  { rejectValue: { message: string; status?: number } }
>(
  'api/fetchEaterRestaurantReviews',
  async ({ eaterId, page = 0, limit = 20 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('limit', limit.toString())
      
      const response = await okaoAxios.get(
        `/eater/${eaterId}/restaurant-reviews?${params.toString()}`,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          message: error.response?.data?.message || 'Unable to load reviews. Try again.',
          status: error.response?.status,
        })
      }
      return rejectWithValue({ message: 'Unable to load reviews. Try again.' })
    }
  },
)

export interface FetchEaterDishReviewsRequest {
  eaterId: string
  page?: number
  limit?: number
}

export interface ProfileReviewsResponse {
  dishReviewItems: Array<{
    dishId: string
    dishName: string
    restaurantAlias: string
    restaurantName: string
    starCount: number
    review?: string
    created: string
    updated: string
  }>
  page: number
  count: number
  totalCount: number
}

export const fetchEaterDishReviews = createAsyncThunk<
  ProfileReviewsResponse,
  FetchEaterDishReviewsRequest,
  { rejectValue: { message: string; status?: number } }
>(
  'api/fetchEaterDishReviews',
  async ({ eaterId, page = 0, limit = 20 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('limit', limit.toString())
      
      const response = await okaoAxios.get(
        `/eater/${eaterId}/dish-reviews?${params.toString()}`,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          message: error.response?.data?.message || 'Unable to load dish reviews. Try again.',
          status: error.response?.status,
        })
      }
      return rejectWithValue({ message: 'Unable to load dish reviews. Try again.' })
    }
  },
)

export interface FetchEaterSavedRestaurantsRequest {
  eaterId: string
}

/**
 * Normalizes saved restaurants response from various backend shapes to RestaurantAndPredictedRestaurant[]
 * Handles:
 * - RestaurantAndPredictedRestaurant[] (array with restaurantDTO field) - return as-is
 * - PredictedRestaurantResponse (has .result array) - return .result
 * - SavedRestaurantResponse[] (lightweight with restaurantId/alias/name/imageUrl) - map to full shape
 */
export function normalizeSavedRestaurantsResponse(
  data: any,
): RestaurantAndPredictedRestaurant[] {
  if (!data) {
    return []
  }

  // Case A: Already in expected format (array of RestaurantAndPredictedRestaurant)
  if (Array.isArray(data)) {
    // Check if first item has restaurantDTO (expected format)
    if (data.length > 0 && data[0]?.restaurantDTO) {
      return data as RestaurantAndPredictedRestaurant[]
    }

    // Case C: Array of SavedRestaurantResponse (lightweight)
    // Map to RestaurantAndPredictedRestaurant format
    return data.map((item: any) => {
      // If it already has restaurantDTO, return as-is
      if (item.restaurantDTO) {
        return item as RestaurantAndPredictedRestaurant
      }

      // Otherwise, construct from lightweight fields
      return {
        restaurantDTO: {
          restaurantId: item.restaurantId || item.id || '',
          alias: item.restaurantAlias || item.alias || '',
          name: item.restaurantName || item.name || '',
          imageUrl: item.imageUrl || '',
          cuisine: item.cuisine || '',
          city: item.city || '',
          state: item.state || '',
          address1: item.address1 || item.address || '',
        } satisfies Restaurant,
        group: false,
      } satisfies RestaurantAndPredictedRestaurant
    })
  }

  // Case B: PredictedRestaurantResponse (has .result array)
  if (data.result && Array.isArray(data.result)) {
    return data.result as RestaurantAndPredictedRestaurant[]
  }

  // Fallback: return empty array if shape is unrecognized
  return []
}

export const fetchEaterSavedRestaurants = createAsyncThunk<
  RestaurantAndPredictedRestaurant[], // Return normalized array
  FetchEaterSavedRestaurantsRequest,
  { rejectValue: { message: string; status?: number } }
>(
  'api/fetchEaterSavedRestaurants',
  async ({ eaterId }, { rejectWithValue }) => {
    try {
      const response = await okaoAxios.get(`/eater/${eaterId}/saved-restaurants`)
      // Normalize response to handle multiple backend shapes (backward compatibility)
      const normalized = normalizeSavedRestaurantsResponse(response.data)
      return normalized
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          message: error.response?.data?.message || 'Unable to load saved restaurants. Try again.',
          status: error.response?.status,
        })
      }
      return rejectWithValue({ message: 'Unable to load saved restaurants. Try again.' })
    }
  },
)

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setIsProfileEdit: (state, action: PayloadAction<boolean>) => {
      state.isProfileEdit = action.payload
    },
    setIsValidData: (state, action: PayloadAction<boolean>) => {
      state.isValidData = action.payload
    },
    setIsUpdateData: (state, action: PayloadAction<boolean>) => {
      state.isUpdate = action.payload
    },
    setIsEditCancelledData: (state, action: PayloadAction<boolean>) => {
      state.isEditCancelled = action.payload
    },
    setCurrentTab: (state, action: PayloadAction<string>) => {
      state.currentTab = action.payload
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload
    },
    setBioPayload: (state, action: PayloadAction<Eater>) => {
      state.bioPayload = action.payload
    },
    setEater: (state, action: PayloadAction<Eater>) => {
      state.eater = action.payload
    },
    setRestaurantReviews: (
      state,
      action: PayloadAction<IRestaurantReview[]>,
    ) => {
      state.eater = { ...state.eater, restaurantReviews: action.payload }
    },
    setEaterDishReviews: (state, action: PayloadAction<DiningReview[]>) => {
      state.eaterDishReviews = action.payload
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload
    },
    setSeverity: (state, action: PayloadAction<AlertColor>) => {
      state.severity = action.payload
    },
    clearData: () => initialState,
    resetRewardsActivity: (state) => {
      state.rewardsActivity = {
        activities: [],
        loading: false,
        error: null,
        page: 1,
        hasMore: false,
        lastFetched: null,
      }
    },
    setRewardsActivityPage: (state, action: PayloadAction<number>) => {
      state.rewardsActivity.page = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateEater.fulfilled, (state, action) => {
        state.eater = action.payload.eaterDTO
        state.status = 'idle'
      })
      .addCase(fetchEater.fulfilled, (state, action) => {
        // CRITICAL: Merge instead of replace to preserve heavy arrays set by other actions
        // This prevents restaurantReviews from disappearing after fetchEater runs
        
        const prev = state.eater ?? {}
        const next = action.payload ?? {}
        
        // Merge payload into existing eater, preserving heavy collections unless explicitly provided
        state.eater = {
          ...prev,
          ...next,
          // Preserve heavy collections unless payload explicitly provides them
          restaurantReviews: next.restaurantReviews ?? prev.restaurantReviews,
          reviews: next.reviews ?? prev.reviews,
          predictedSavedRestaurants: next.predictedSavedRestaurants ?? prev.predictedSavedRestaurants,
          savedRestaurants: next.savedRestaurants ?? prev.savedRestaurants,
        }
        
        // Update savedRestaurantIds safely:
        // Only set if payload provides predictedSavedRestaurants, else preserve existing
        if (next.predictedSavedRestaurants?.result) {
          state.savedRestaurantIds =
            next.predictedSavedRestaurants.result.map(
              (restaurant) => restaurant?.restaurantDTO?.restaurantId,
            )
        }
        // Otherwise preserve existing savedRestaurantIds (don't clear it)

        state.status = 'idle'
        state.isProfileLoading = false
      })
      .addCase(favoriteRestaurant.fulfilled, (state, action) => {
        // favoriteRestaurant returns RestaurantDTO, not updated eater profile
        // Use optimistic update: toggle savedRestaurantIds immediately
        // Safeguard: handle missing restaurantId gracefully (no crash)
        const restaurantId = action.payload?.restaurantId
        if (!restaurantId) {
          // No-op: payload missing restaurantId, preserve existing state
          state.status = 'idle'
          state.severity = 'error'
          state.message = 'Unable to update saved restaurant. Please try again.'
          return
        }

        const index = state.savedRestaurantIds.indexOf(restaurantId)

        if (index !== -1) {
          // Remove from saved list
          state.savedRestaurantIds.splice(index, 1)
          // Also update predictedSavedRestaurants if it exists
          if (
            state.eater &&
            state.eater.predictedSavedRestaurants &&
            state.eater.predictedSavedRestaurants.result
          ) {
            state.eater.predictedSavedRestaurants.result =
              state.eater.predictedSavedRestaurants.result.filter(
                (restaurant) =>
                  restaurant.restaurantDTO?.restaurantId !== restaurantId,
              )
          }
          // Remove from savedRestaurants list if it exists
          if (state.savedRestaurants.restaurants) {
            state.savedRestaurants.restaurants =
              state.savedRestaurants.restaurants.filter(
                (r) => r.restaurantDTO?.restaurantId !== restaurantId,
              )
          }
        } else {
          // Add to saved list
          state.savedRestaurantIds.push(restaurantId)
          // Note: We don't add to predictedSavedRestaurants here because we don't have full restaurant data
          // The savedRestaurants list will be refetched in background
          // Trigger background refetch if eaterId exists
          // (This will be handled by ProfileSavedRestaurants component when it detects the change)
        }
        state.status = 'idle'
        state.severity = 'success'
      })
      .addCase(fetchEaterSavedRestaurants.pending, (state) => {
        state.savedRestaurants.loading = true
        state.savedRestaurants.error = null
      })
      .addCase(fetchEaterSavedRestaurants.fulfilled, (state, action) => {
        // Payload is already normalized RestaurantAndPredictedRestaurant[] from thunk
        state.savedRestaurants.restaurants = action.payload
        state.savedRestaurants.loading = false
        state.savedRestaurants.error = null
        state.savedRestaurants.lastFetched = Date.now()
        
        // Update savedRestaurantIds from the payload
        state.savedRestaurantIds = action.payload
          .map((r) => r.restaurantDTO?.restaurantId)
          .filter((id): id is string => Boolean(id))
        
        // Only update predictedSavedRestaurants.result if it already exists
        // DO NOT create it if it doesn't exist (to avoid breaking other screens)
        if (state.eater?.predictedSavedRestaurants?.result) {
          state.eater.predictedSavedRestaurants.result = action.payload
        }
        // If predictedSavedRestaurants doesn't exist, leave it alone
      })
      .addCase(fetchEaterSavedRestaurants.rejected, (state, action) => {
        state.savedRestaurants.loading = false
        state.savedRestaurants.error = action.payload?.message ?? 'Unable to load saved restaurants. Try again.'
      })
      .addCase(recoverUserAccount.fulfilled, (state, action) => {
        state.eater = action.payload.data.eaterDTO
        // Only update savedRestaurantIds if predictedSavedRestaurants exists
        if (action.payload.data?.eaterDTO?.predictedSavedRestaurants?.result) {
          state.savedRestaurantIds =
            action.payload.data.eaterDTO.predictedSavedRestaurants.result.map(
              (restaurant) => restaurant?.restaurantDTO?.restaurantId,
            )
        }
        state.status = 'idle'
      })
      .addCase(restaurantOnboardRequest.fulfilled, (state, action) => {
        state.eater = action.payload.eaterDTO
        // Only update savedRestaurantIds if predictedSavedRestaurants exists
        if (action.payload?.eaterDTO?.predictedSavedRestaurants?.result) {
          state.savedRestaurantIds =
            action.payload.eaterDTO.predictedSavedRestaurants.result.map(
              (restaurant) => restaurant?.restaurantDTO?.restaurantId,
            )
        }
        state.status = 'idle'
      })
      .addCase(updateRestaurantOnboard.fulfilled, (state, action) => {
        state.eater = action.payload.eaterDTO
        // Only update savedRestaurantIds if predictedSavedRestaurants exists
        if (action.payload.eaterDTO?.predictedSavedRestaurants?.result) {
          state.savedRestaurantIds =
            action.payload.eaterDTO.predictedSavedRestaurants.result.map(
              (restaurant) => restaurant?.restaurantDTO?.restaurantId,
            )
        }
        state.status = 'idle'
      })

      // loginAudit.fulfilled removed - hydration moved to fetchSession.fulfilled
      // loginAudit now returns lite payload, so we don't hydrate profileSlice here
      .addCase(fetchSession.fulfilled, (state, action) => {
        // Hydrate lite-only profile data from session response
        // Session now returns minimal fields: eaterId, step, subscribed, imageUrl, status
        // Heavy collections (reviews, restaurantReviews, predictedSavedRestaurants) are NOT included
        if (action.payload.eaterDTO !== null && action.payload.eaterDTO !== undefined) {
          // Helper to remove undefined values (preserves false, 0, null, empty strings)
          const removeUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
            const result: Partial<T> = {}
            for (const key in obj) {
              if (obj[key] !== undefined) {
                result[key] = obj[key]
              }
            }
            return result
          }

          // Merge with existing eater data, preserving any fields not in lite response
          // Remove undefined values to avoid overwriting existing data
          // Do NOT set heavy collections from session - they should be fetched on-demand
          state.eater = {
            ...(state.eater ?? {}),
            ...removeUndefined(action.payload.eaterDTO),
          }
          // Do NOT set savedRestaurantIds or eaterDishReviews from session
          // These should be fetched on-demand if needed
          // NOTE: account_created is fired in BaseLayout from the authoritative `isNewUser` flag
          // returned by /login-lite (set only on the login that actually provisions the account).
        }
        state.status = 'idle'
      })
      .addCase(updateEaterProfile.fulfilled, (state, action) => {
        if (action.payload.eaterDTO !== null && state.eater) {
          const {
            ingredientLikes,
            ingredientDislikes,
            cuisineLikes,
            cuisineDislikes,
            foodPreparationLiked,
            foodPreparationDisliked,
            ambienceLiked,
            ambienceDisliked,
            diningFrequency,
            dietaryPreferences,
            allergies,
            favoriteRestaurants,
            favoriteReviewers,
            x,
            facebook,
            instagram,
            tiktok,
          } = action.payload.eaterDTO
          state.eater.ingredientLikes = ingredientLikes
          state.eater.ingredientDislikes = ingredientDislikes
          state.eater.cuisineLikes = cuisineLikes
          state.eater.cuisineDislikes = cuisineDislikes
          state.eater.foodPreparationLiked = foodPreparationLiked
          state.eater.foodPreparationDisliked = foodPreparationDisliked
          state.eater.ambienceLiked = ambienceLiked
          state.eater.ambienceDisliked = ambienceDisliked
          state.eater.diningFrequency = diningFrequency
          state.eater.dietaryPreferences = dietaryPreferences
          state.eater.allergies = allergies
          state.eater.favoriteRestaurants = favoriteRestaurants
          state.eater.favoriteReviewers = favoriteReviewers
          state.eater.x = x
          state.eater.facebook = facebook
          state.eater.instagram = instagram
          state.eater.tiktok = tiktok
        }
        state.status = 'idle'
      })
      .addCase(updateEaterImage.fulfilled, (state, action) => {
        state.status = 'idle'
      })
      .addCase(awardInstagramPoints.fulfilled, (state, action) => {
        const { awarded, points, hasClaimedInstagramBonus } = action.payload
        if (state.eater) {
          if (awarded && points !== undefined) {
            // Update eater's points balance
            state.eater = {
              ...state.eater,
              points,
              hasClaimedInstagramBonus: hasClaimedInstagramBonus ?? true,
              instagramBonusClaimed: true,
            }
            state.shouldOpen = true
            state.message = `You earned +50 points for checking us out on Instagram!`
            state.severity = 'success'
          } else if (!awarded && hasClaimedInstagramBonus !== undefined) {
            // User already claimed but points weren't awarded
            state.eater = {
              ...state.eater,
              hasClaimedInstagramBonus,
              instagramBonusClaimed: true,
            }
          }
        }
        state.status = 'idle'
      })
      .addCase(fetchRewardsActivity.pending, (state) => {
        state.rewardsActivity.loading = true
        state.rewardsActivity.error = null
      })
      .addCase(fetchRewardsActivity.fulfilled, (state, action) => {
        const response = action.payload
        const rawItems = response?.data ?? response?.activities ?? response?.items ?? []
        
        // Check if items are already in PointsHistoryItemDTO format (have label and timestamp)
        const firstItem = rawItems[0] as any
        const isNormalized = rawItems.length > 0 && firstItem?.label && firstItem?.timestamp
        
        let normalizedItems: PointsHistoryItemDTO[]
        if (isNormalized) {
          // Already normalized - just sanitize PII
          normalizedItems = rawItems.map((item: any) => {
            const { customerId, appId, appToken, email, ...sanitized } = item
            return sanitized as PointsHistoryItemDTO
          })
        } else {
          // Map from Voucherify format to normalized format
          const mapped = mapVoucherifyActivityResponse({ items: rawItems, hasMore: response?.hasMore })
          normalizedItems = mapped.items
        }
        
        // If this is page 0 (initial load or refresh), replace activities
        // Otherwise, append for pagination (page > 0)
        if (state.rewardsActivity.page === 0) {
          state.rewardsActivity.activities = normalizedItems as any
        } else {
          state.rewardsActivity.activities = [...state.rewardsActivity.activities, ...normalizedItems] as any
        }
        
        state.rewardsActivity.loading = false
        state.rewardsActivity.error = null
        // Use hasMore from response if present, otherwise treat as no pagination
        state.rewardsActivity.hasMore = response?.hasMore ?? false
        state.rewardsActivity.page = response?.page ?? 0
        state.rewardsActivity.lastFetched = Date.now()
      })
      .addCase(fetchRewardsActivity.rejected, (state, action) => {
        state.rewardsActivity.loading = false
        state.rewardsActivity.error = action.payload?.message ?? 'Unable to load activity. Try again.'
      })
      .addCase(sendPayoutLinkSms.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(sendPayoutLinkSms.fulfilled, (state) => {
        state.status = 'idle'
      })
      .addCase(sendPayoutLinkSms.rejected, (state, action) => {
        state.status = 'idle'
        // Error handling is done in component via toast
      })
      .addCase(fetchEaterRestaurantReviews.pending, (state) => {
        state.profileRestaurantReviews.loading = true
        state.profileRestaurantReviews.error = null
      })
      .addCase(fetchEaterRestaurantReviews.fulfilled, (state, action) => {
        const { result, page, totalCount, count } = action.payload
        // If this is page 0 (initial load), replace reviews
        // Otherwise, append for pagination
        if (page === 0) {
          state.profileRestaurantReviews.reviews = result
        } else {
          state.profileRestaurantReviews.reviews = [
            ...state.profileRestaurantReviews.reviews,
            ...result,
          ]
        }
        state.profileRestaurantReviews.loading = false
        state.profileRestaurantReviews.error = null
        state.profileRestaurantReviews.page = page
        state.profileRestaurantReviews.limit = count
        state.profileRestaurantReviews.totalCount = totalCount
        state.profileRestaurantReviews.hasMore = count < totalCount
      })
      .addCase(fetchEaterRestaurantReviews.rejected, (state, action) => {
        state.profileRestaurantReviews.loading = false
        state.profileRestaurantReviews.error = action.payload?.message ?? 'Unable to load reviews. Try again.'
      })
      .addCase(fetchEaterDishReviews.pending, (state) => {
        state.profileDishReviews.loading = true
        state.profileDishReviews.error = null
      })
      .addCase(saveRestaurantDish.fulfilled, (state, action) => {
        // Immediately merge the returned dish review into eaterDishReviews
        // This ensures Reviewed Dishes updates instantly when user rates a dish
        const newReview = action.payload
        
        // Convert DiningReviewResponse to DiningReview format
        const diningReview: DiningReview = {
          diningReviewId: newReview.diningReviewId,
          restaurantAlias: newReview.restaurantAlias,
          dishId: newReview.dishId,
          value: newReview.value ?? '',
          sentimentLabel: newReview.sentimentLabel ?? '',
          sentimentScore: newReview.sentimentScore ?? 0,
          sentimentSummary: newReview.sentimentSummary ?? '',
          sentimentTags: newReview.sentimentTags ?? '',
          name: newReview.name ?? '',
          email: newReview.email ?? '',
          location: newReview.location ?? '',
          menuId: newReview.menuId ?? '',
          created: newReview.created ?? new Date().toISOString(),
          updated: newReview.updated ?? new Date().toISOString(),
          starCount: newReview.starCount,
        }
        
        // Remove any existing review for this dishId
        // Check both dishId and dish?.dishID for compatibility with ReviewsTab mapping
        const existing = state.eaterDishReviews ?? []
        const filtered = existing.filter(
          (r) =>
            r.dishId !== newReview.dishId &&
            (r as any).dish?.dishID !== newReview.dishId,
        )
        
        // Add the new review
        state.eaterDishReviews = [...filtered, diningReview]
      })
      .addCase(fetchEaterDishReviews.fulfilled, (state, action) => {
        const { dishReviewItems, page, totalCount, count } = action.payload
        // If this is page 0 (initial load), replace reviews
        // Otherwise, append for pagination
        if (page === 0) {
          state.profileDishReviews.reviews = dishReviewItems
        } else {
          state.profileDishReviews.reviews = [
            ...state.profileDishReviews.reviews,
            ...dishReviewItems,
          ]
        }
        state.profileDishReviews.loading = false
        state.profileDishReviews.error = null
        state.profileDishReviews.page = page
        state.profileDishReviews.limit = count
        state.profileDishReviews.totalCount = totalCount
        state.profileDishReviews.hasMore = count < totalCount
      })
      .addCase(fetchEaterDishReviews.rejected, (state, action) => {
        state.profileDishReviews.loading = false
        state.profileDishReviews.error = action.payload?.message ?? 'Unable to load dish reviews. Try again.'
      })
      // Saved menus
      .addCase(fetchSavedMenus.pending, (state) => {
        state.savedMenus.loading = true
        state.savedMenus.error = null
      })
      .addCase(fetchSavedMenus.fulfilled, (state, action) => {
        state.savedMenus.loading = false
        state.savedMenus.items = action.payload.items
        state.savedMenus.page = action.payload.page
        state.savedMenus.size = action.payload.size
        state.savedMenus.totalElements = action.payload.totalElements
        state.savedMenus.totalPages = action.payload.totalPages
        state.savedMenus.hasMore = action.payload.page < action.payload.totalPages - 1
        state.savedMenus.lastFetched = Date.now()
        state.savedMenus.error = null
      })
      .addCase(fetchSavedMenus.rejected, (state, action) => {
        state.savedMenus.loading = false
        state.savedMenus.error = action.payload?.message ?? 'Unable to load saved menus. Try again.'
      })
      .addCase(awardInstagramPoints.rejected, (state, action) => {
        const payload = action.payload ?? { message: 'Oops! We had trouble awarding your Instagram bonus. Please try again.' }
        const { message, alreadyClaimed } = payload
        
        if (alreadyClaimed && state.eater) {
          // Mark as claimed and show success message
          state.eater = {
            ...state.eater,
            instagramBonusClaimed: true,
            hasClaimedInstagramBonus: true,
          }
          state.shouldOpen = true
          state.message = "You've already earned this Instagram bonus 🎉"
          state.severity = 'success'
        } else {
          state.shouldOpen = true
          state.message = message
          state.severity = 'error'
        }
        state.status = 'error'
      })
      .addMatcher(
        isAnyOf(
          updateEater.rejected,
          updateEaterImage.rejected,
          fetchEater.rejected,
          awardInstagramPoints.rejected,
        ),
        (state, action) => {
          state.shouldOpen = true
          state.message =
            'Sorry an error occurred while updating your user. Please try again'
          state.status = 'error'
          state.severity = 'error'
          state.isProfileLoading = false
        },
      )
      .addMatcher(
        isAnyOf(
          updateEater.pending,
          fetchEater.pending,
          updateEaterImage.pending,
          awardInstagramPoints.pending,
        ),
        (state, action) => {
          state.status = 'loading'
        },
      )
      .addMatcher(isAnyOf(fetchEater.pending), (state, action) => {
        state.isProfileLoading = true
      })
  },
})

export const selectIsProfileEdit = (state: RootState): boolean =>
  state.profile?.isProfileEdit
export const selectIsValidData = (state: RootState): boolean =>
  state.profile?.isValidData
export const selectIsUpdateData = (state: RootState): boolean =>
  state.profile?.isUpdate
export const selectIsEditCancelledData = (state: RootState): boolean =>
  state.profile?.isEditCancelled
export const selectCurrentTab = (state: RootState): string =>
  state.profile?.currentTab
export const selectIsLoading = (state: RootState): boolean =>
  state.profile.status === 'loading'
export const selectIsProfileLoading = (state: RootState): boolean =>
  state.profile.isProfileLoading
export const selectShouldOpen = (state: RootState): boolean =>
  state.profile.shouldOpen
export const selectMessage = (state: RootState): string | undefined =>
  state.profile.message
export const selectSeverity = (state: RootState): AlertColor | undefined =>
  state.profile.severity
export const selectEater = (state: RootState): Eater | undefined =>
  state.profile.eater

// Selector to get subscription status from profile slice (fallback to login state)
export const selectEaterSubscribedFromProfile = (state: RootState): boolean | undefined =>
  state.profile.eater?.subscribed
export const selectBioPayload = (state: RootState): Eater =>
  state.profile?.bioPayload
export const selectSavedRestaurantIds = (state: RootState): string[] =>
  state.profile.savedRestaurantIds
export const selectEaterDishReviews = (state: RootState): DiningReview[] =>
  state.profile.eaterDishReviews

// eslint-disable-next-line
export const selectProfileDishReviews = (state: RootState) =>
  state.profile.profileDishReviews.reviews

export const selectProfileDishReviewsLoading = (state: RootState): boolean =>
  state.profile.profileDishReviews.loading

export const selectProfileDishReviewsError = (state: RootState): string | null =>
  state.profile.profileDishReviews.error

// eslint-disable-next-line
export const selectProfileDishReviewsPagination = (state: RootState) => ({
  page: state.profile.profileDishReviews.page,
  limit: state.profile.profileDishReviews.limit,
  totalCount: state.profile.profileDishReviews.totalCount,
  hasMore: state.profile.profileDishReviews.hasMore,
})

export const selectProfileRestaurantReviews = (state: RootState): IRestaurantReview[] =>
  state.profile.profileRestaurantReviews.reviews

export const selectProfileRestaurantReviewsLoading = (state: RootState): boolean =>
  state.profile.profileRestaurantReviews.loading

export const selectProfileRestaurantReviewsError = (state: RootState): string | null =>
  state.profile.profileRestaurantReviews.error

// eslint-disable-next-line
export const selectProfileRestaurantReviewsPagination = (state: RootState) => ({
  page: state.profile.profileRestaurantReviews.page,
  limit: state.profile.profileRestaurantReviews.limit,
  totalCount: state.profile.profileRestaurantReviews.totalCount,
  hasMore: state.profile.profileRestaurantReviews.hasMore,
})

export const selectSavedRestaurants = (state: RootState): RestaurantAndPredictedRestaurant[] =>
  state.profile.savedRestaurants.restaurants

export const selectSavedRestaurantsLoading = (state: RootState): boolean =>
  state.profile.savedRestaurants.loading

export const selectSavedRestaurantsError = (state: RootState): string | null =>
  state.profile.savedRestaurants.error

// eslint-disable-next-line
export const selectRewardsActivity = (state: RootState) =>
  state.profile.rewardsActivity

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectSavedMenus = (state: RootState) =>
  state.profile.savedMenus

export const {
  setBioPayload,
  setIsProfileEdit,
  setIsValidData,
  setIsUpdateData,
  setIsEditCancelledData,
  setCurrentTab,
  setOpen,
  setEater,
  setRestaurantReviews,
  setEaterDishReviews,
  setMessage,
  setSeverity,
  clearData,
  resetRewardsActivity,
  setRewardsActivityPage,
} = profileSlice.actions

export default profileSlice.reducer
